import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, Plug, Plus } from 'lucide-react';
import {
    Badge,
    Button,
    ConfirmDialog,
    CopyableId,
    EmptyState,
    ErrorState,
    Loading,
} from '@fiado/react-kit';
import {
    hasNoIpRestriction,
    integrationsApi,
    type Integration,
} from '../../../../api/endpoints/integrations';
import { tenantApi } from '../../../../api/endpoints/tenant';
import { handleApiError } from '../../../../api/errorMapping';
import { Gate } from '../../../../components/permissions/Gate';
import { formatDateMx } from '../../../../lib/dateHelpers';
import { integrationsKey, useIntegrations } from '../../../../hooks/useIntegrations';
import { CreateIntegrationModal } from './CreateIntegrationModal';
import { IntegrationKeysModal } from './IntegrationKeysModal';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). Las integraciones son de ESTE tenant. */
    tenantId: string;
}

/** Pestaña Integraciones del drill-down de un tenant. Solo la administran usuarios de plataforma. */
export function IntegrationsTable({ tenantId }: Props) {
    const queryClient = useQueryClient();
    const [createOpen, setCreateOpen] = useState(false);
    const [keysFor, setKeysFor] = useState<Integration | null>(null);
    const [toSuspend, setToSuspend] = useState<Integration | null>(null);

    const { data: integrations, isLoading, isError, refetch } = useIntegrations(tenantId);

    // roleId → nombre legible. Se resuelve contra el tenant del DRILL-DOWN, no el del actor.
    const { data: roles } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'roles'],
        queryFn: () => tenantApi.listRoles(tenantId),
    });
    const roleNameById = useMemo(
        () => new Map((roles?.items ?? []).map((r) => [r.roleId, r.roleName])),
        [roles],
    );

    const invalidate = () => queryClient.invalidateQueries({ queryKey: integrationsKey(tenantId) });

    const statusMut = useMutation({
        mutationFn: ({ integration, activate }: { integration: Integration; activate: boolean }) =>
            activate
                ? integrationsApi.activate(tenantId, integration.integrationId)
                : integrationsApi.suspend(tenantId, integration.integrationId),
        onSuccess: async (row) => {
            toast.success(
                row.status === 'ACTIVE'
                    ? 'Integración reactivada. Sus llaves activas vuelven a funcionar.'
                    : 'Integración suspendida. Todas sus llaves dejan de funcionar.',
            );
            await invalidate();
        },
        onError: (error) => {
            handleApiError(error);
        },
    });

    if (isLoading) return <Loading />;

    // Sin esto, una lectura caída se vería igual que "este tenant no tiene integraciones".
    if (isError) {
        return (
            <ErrorState
                title="No se pudieron cargar las integraciones"
                message="No pudimos leer las credenciales de este tenant. Vuelve a intentar."
                onRetry={() => void refetch()}
                retryLabel="Reintentar"
            />
        );
    }

    const items = integrations ?? [];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <p className="max-w-2xl text-sm text-muted-foreground">
                    Credenciales para que un sistema externo consuma las APIs de este tenant. Cada
                    integración porta un rol del tenant, y sus permisos son exactamente los de ese
                    rol.
                </p>
                <Gate permission="platform.integration.manage" scope="TENANT" scopeRef={tenantId}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                        leftIcon={<Plus className="h-4 w-4" />}
                    >
                        Nueva integración
                    </Button>
                </Gate>
            </div>

            {items.length === 0 ? (
                <EmptyState
                    title="Este tenant no tiene integraciones"
                    description="Crea una cuando un sistema externo necesite consumir sus APIs."
                />
            ) : (
                <ul className="divide-y rounded-lg border">
                    {items.map((it) => {
                        const withoutIpFilter = hasNoIpRestriction(it);
                        return (
                            <li
                                key={it.integrationId}
                                className="flex flex-wrap items-center justify-between gap-3 p-4"
                            >
                                <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Plug
                                            className="h-4 w-4 text-muted-foreground"
                                            aria-hidden
                                        />
                                        <span className="font-medium">{it.displayName}</span>
                                        <Badge
                                            variant={it.status === 'ACTIVE' ? 'success' : 'danger'}
                                        >
                                            {it.status === 'ACTIVE' ? 'Activa' : 'Suspendida'}
                                        </Badge>
                                        {/* La protección por IP se puede apagar, pero nunca en silencio. */}
                                        {withoutIpFilter && (
                                            <Badge variant="warning">
                                                <Globe className="mr-1 h-3 w-3" aria-hidden />
                                                Sin restricción de IP
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Rol{' '}
                                        {roleNameById.get(it.roleId) ?? (
                                            <CopyableId value={it.roleId} label="ID del rol" />
                                        )}
                                        {' · '}creada {formatDateMx(it.createdAt)}
                                        {!withoutIpFilter &&
                                            ` · ${String(it.ipAllowlist.length)} IP(s) permitidas`}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <Gate
                                        permission="platform.integration.view"
                                        scope="TENANT"
                                        scopeRef={tenantId}
                                    >
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setKeysFor(it);
                                            }}
                                        >
                                            Llaves
                                        </Button>
                                    </Gate>
                                    <Gate
                                        permission="platform.integration.manage"
                                        scope="TENANT"
                                        scopeRef={tenantId}
                                    >
                                        {it.status === 'ACTIVE' ? (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => {
                                                    setToSuspend(it);
                                                }}
                                            >
                                                Suspender
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                isLoading={statusMut.isPending}
                                                onClick={() => {
                                                    statusMut.mutate({
                                                        integration: it,
                                                        activate: true,
                                                    });
                                                }}
                                            >
                                                Reactivar
                                            </Button>
                                        )}
                                    </Gate>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            <CreateIntegrationModal
                tenantId={tenantId}
                open={createOpen}
                onOpenChange={setCreateOpen}
            />

            <IntegrationKeysModal
                tenantId={tenantId}
                integration={keysFor}
                onClose={() => {
                    setKeysFor(null);
                }}
            />

            <ConfirmDialog
                open={toSuspend !== null}
                onOpenChange={(open) => {
                    if (!open) setToSuspend(null);
                }}
                title={`¿Suspender ${toSuspend?.displayName ?? ''}?`}
                description="Todas sus llaves dejan de funcionar de inmediato y el socio va a empezar a recibir 401. Es reversible: al reactivarla, las llaves que sigan activas vuelven a servir."
                confirmLabel="Suspender"
                destructive
                loading={statusMut.isPending}
                onConfirm={() => {
                    if (toSuspend) statusMut.mutate({ integration: toSuspend, activate: false });
                    setToSuspend(null);
                }}
            />
        </div>
    );
}
